import React from 'react';
import cx from 'classnames';
import Link from 'components/link/link';
import A11yVisuallyHidden from 'components/a11y/a11y-visually-hidden';
import { P1, P2, P3 } from 'components/typography';
import { Lexeme } from '@bumble/localization/components';

enum ButtonSize {
    SMALL,
    MEDIUM,
    LARGE,
    XLARGE,
    STRETCH,
}

enum ButtonDesignType {
    NONE,
    FILLED,
    STROKE,
    TRANSPARENT,
    SEMITRANSPARENT,
    MONOCHROME,
    LINK,
}

export enum ButtonTheme {
    NONE = 'NONE',
    ONE = 'ONE',
    TWO = 'TWO',
    THREE = 'THREE',
    FOUR = 'FOUR',
    FIVE = 'FIVE',
}

const mapThemeToClassname = {
    [ButtonTheme.NONE]: '',
    [ButtonTheme.ONE]: 'button--color-one',
    [ButtonTheme.TWO]: 'button--color-two',
    [ButtonTheme.THREE]: 'button--color-three',
    [ButtonTheme.FOUR]: 'button--color-four',
    [ButtonTheme.FIVE]: 'button--color-five',
};

export const mapDesignTypeToClassname: { [key in ButtonDesignType]: string } = {
    [ButtonDesignType.NONE]: '',
    [ButtonDesignType.FILLED]: 'button--filled',
    [ButtonDesignType.STROKE]: 'button--stroke',
    [ButtonDesignType.TRANSPARENT]: 'button--transparent',
    [ButtonDesignType.SEMITRANSPARENT]: 'button--semitransparent',
    [ButtonDesignType.MONOCHROME]: 'button--monochrome',
    [ButtonDesignType.LINK]: 'button--link',
};

const mapSizeToClassname: { [key in ButtonSize]: string } = {
    [ButtonSize.SMALL]: 'button--sm',
    [ButtonSize.MEDIUM]: '',
    [ButtonSize.LARGE]: 'button--lg',
    [ButtonSize.XLARGE]: 'button--xlg',
    [ButtonSize.STRETCH]: 'button--stretch',
};

const mapSizeToTag: { [key in ButtonSize]: React.FC<React.PropsWithChildren> } = {
    [ButtonSize.SMALL]: P3,
    [ButtonSize.MEDIUM]: P2,
    [ButtonSize.LARGE]: P1,
    [ButtonSize.XLARGE]: P2,
    [ButtonSize.STRETCH]: React.Fragment,
};

enum ButtonColor {
    NONE,
    Primary,
    White,
    Dark,
}

const mapColorToClassname = {
    [ButtonColor.NONE]: '',
    [ButtonColor.Primary]: 'button--primary',
    [ButtonColor.White]: 'button--white',
    [ButtonColor.Dark]: 'button--color-dark',
};

type ButtonType = React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
>['type'];

type ButtonProps = {
    media?: React.ReactNode;
    icon?: React.ReactNode;
    html?: string;
    text?: string | React.ReactNode;
    labelText?: string | React.ReactNode;
    href?: string;
    hasExtraVgap?: boolean;
    extraClass?: string;
    jsClass?: string;
    color?: ButtonColor;
    size?: ButtonSize;
    designType?: ButtonDesignType;
    isCircle?: boolean;
    inline?: boolean;
    target?: string;
    rel?: string;
    type?: ButtonType;
    isApp?: boolean;
    onClick?: (event?: any) => void;
    theme?: ButtonTheme;
    seo?: {
        name?: string;
        category?: string;
        action?: string;
        label?: string;
        source?: string;
    };
};

function Button(props: ButtonProps) {
    const {
        media,
        icon,
        html,
        labelText,
        text,
        href,
        hasExtraVgap,
        extraClass,
        jsClass,
        color = ButtonColor.NONE,
        size = ButtonSize.MEDIUM,
        designType = ButtonDesignType.NONE,
        isCircle,
        inline,
        isApp,
        onClick,
        target,
        rel,
        type,
        theme = ButtonTheme.NONE,
        seo = {
            name: null,
            category: null,
            action: null,
            label: null,
            source: null,
        },
    } = props;

    const className = cx({
        button: true,
        'button--circle': isCircle,
        'button--app': isApp,
        'button--extra-vgap': hasExtraVgap,
        'button--inline': inline,
        [mapThemeToClassname[theme]]: true,
        [mapColorToClassname[color]]: true,
        [mapSizeToClassname[size]]: true,
        [mapDesignTypeToClassname[designType]]: true,
        [jsClass as string]: jsClass,
        [extraClass as string]: extraClass,
    });

    /**
     * https://html.spec.whatwg.org/multipage/links.html#link-type-noreferrer
     */
    let buttonRel = rel;

    if (target === '_blank') {
        buttonRel = 'noreferrer';
    }

    const TextTag = mapSizeToTag[size];

    const buttonContent = (
        <span className="button__content">
            {media ? <span className="button__media">{media}</span> : null}

            {icon ? <span className="button__icon">{icon}</span> : null}

            {text ? (
                <span className="button__text">
                    <TextTag>{text}</TextTag>
                </span>
            ) : null}

            {html ? (
                <span className="button__text">
                    <TextTag>
                        <span
                            dangerouslySetInnerHTML={
                                html
                                    ? {
                                          __html: html,
                                      }
                                    : undefined
                            }
                        />
                    </TextTag>
                </span>
            ) : null}
        </span>
    );

    if (href) {
        return (
            <Link
                aria-label={labelText?.toString()}
                className={className}
                href={href}
                target={target}
                rel={buttonRel}
                type={type}
                data-seo-name={seo.name}
                data-seo-category={seo.category}
                data-seo-action={seo.action}
                data-seo-label={seo.label}
                data-source={seo.source}
                onClick={onClick}
            >
                {buttonContent}

                {target === '_blank' ? (
                    <A11yVisuallyHidden>
                        <Lexeme path="mw@link.a11y.label.new-window" />
                    </A11yVisuallyHidden>
                ) : null}
            </Link>
        );
    }

    return (
        <button
            aria-label={labelText?.toString()}
            className={className}
            type={type}
            data-seo-name={seo.name}
            data-seo-category={seo.category}
            data-seo-action={seo.action}
            data-seo-label={seo.label}
            data-source={seo.source}
            onClick={onClick}
        >
            {buttonContent}
        </button>
    );
}
Button.color = ButtonColor;
Button.size = ButtonSize;
Button.designType = ButtonDesignType;
Button.Theme = ButtonTheme;

export default Button;
